<template>
  <system-frame :title="csc ? 'SMS (CSC Telecom)' : 'SMS (Twilio)'" :hint="csc ? 'Konto saldo, selle kuu SMS-id, saatja nimi ja saadetud sõnumid.' : 'Konto saldo, saatja number ja saadetud sõnumid.'">

    <!-- What the account has left, and the number the reminders go out from.
         CSC: its balance, and this month from intra's own log. -->
    <card class="mb-6">
      <div class="d-flex align-center flex-wrap ga-4">
        <template v-if="csc">
          <div>
            <div class="text-grey-darken-1 text-body-2">Saldo</div>
            <div v-if="balance.balance" class="text-h5 font-weight-medium">{{ money(balance.balance.money) }} {{ balance.balance.currency }}</div>
            <div v-else class="text-h5 text-grey">—</div>
          </div>
          <div>
            <div class="text-grey-darken-1 text-body-2">SMS-e sel kuul</div>
            <div class="text-h5 font-weight-medium">{{ balance.month.count }}<span v-if="balance.month.parts !== balance.month.count" class="text-body-1 text-grey-darken-1"> ({{ balance.month.parts }} osa)</span></div>
          </div>
          <div>
            <div class="text-grey-darken-1 text-body-2">Hinnanguline kulu</div>
            <div class="text-h5 font-weight-medium">≈ {{ euro(balance.month.cost) }} €</div>
            <div class="text-caption text-grey-darken-1">{{ money(balance.price) }} € osa kohta, km-ta</div>
          </div>
          <div>
            <div class="text-grey-darken-1 text-body-2">Saatja nimi</div>
            <div class="text-body-1">{{ balance.sender }}</div>
          </div>
        </template>
        <div v-else>
          <div class="text-grey-darken-1 text-body-2">Saldo</div>
          <div v-if="balance" class="text-h5 font-weight-medium">{{ money(balance.balance) }} {{ balance.currency }}</div>
          <div v-else-if="balanceLoading" class="text-h5 text-grey">…</div>
          <div v-else class="text-h5 text-grey">—</div>
        </div>
        <div v-if="!csc && balance && balance.sender">
          <div class="text-grey-darken-1 text-body-2">Saatja number</div>
          <div class="text-body-1">{{ balance.sender }}</div>
        </div>
        <v-spacer />
        <v-btn variant="text" :loading="balanceLoading" @click="loadBalance">
          <v-icon start>mdi-refresh</v-icon>Värskenda
        </v-btn>
      </div>
      <v-alert v-if="balanceError" type="warning" variant="tonal" density="compact" class="mt-4 mb-0">
        {{ balanceError }}
      </v-alert>
      <v-alert v-if="csc && balance.balanceError" type="warning" variant="tonal" density="compact" class="mt-4 mb-0">
        CSC saldot ei saanud küsida: {{ balance.balanceError }}
      </v-alert>
      <v-alert v-if="csc && balance.lowBalanceAt" type="warning" variant="tonal" density="compact" class="mt-4 mb-0">
        CSC keeldus {{ formatDate(balance.lowBalanceAt) }}: {{ balance.lowBalanceText }}. Lisa krediiti CSC kontol.
      </v-alert>
    </card>

    <card>
      <div class="text-h6">Saadetud SMS-id</div>
      <p class="text-grey-darken-1 mt-2 mb-0">
        <template v-if="csc">
          Intra enda logi: iga SMS, mis valitud päevadel saadeti, koos olekuga, mida
          intra küsib CSC-lt iga 5 minuti järel (3 päeva jooksul). Hind on hinnang.
        </template>
        <template v-else>
          Twilio andmed: iga SMS, mis selle kontoga valitud päevadel saadeti, koos
          oleku ja hinnaga.
        </template>
        Kuupäevad on Eesti aja järgi, mõlemad päevad kaasa arvatud.
      </p>
      <div class="d-flex align-center flex-wrap ga-4 mt-4">
        <date-field
          v-model="from"
          label="Alates"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 190px"
        />
        <date-field
          v-model="to"
          label="Kuni"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 190px"
        />
        <v-select
          v-model="statuses"
          :items="statusOptions"
          label="Olek"
          placeholder="Kõik"
          persistent-placeholder
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          variant="outlined"
          hide-details
          style="min-width: 220px; max-width: 420px"
        />
        <v-btn color="primary" :loading="loading && !messages.length" @click="load">Näita</v-btn>
        <span v-if="messages.length" class="text-body-2 text-grey-darken-1">
          {{ shown.length }} SMS-i<template v-if="statuses.length"> ({{ messages.length }}-st)</template><template v-if="totalPrice !== null">, kokku {{ money(totalPrice) }} {{ priceUnit }}</template>
        </span>
      </div>
      <v-alert v-if="statuses.length && next && !loading" type="info" variant="tonal" density="compact" class="mt-4 mb-0">
        Valitud päevadel on rohkem SMS-e, kui korraga laaditakse ({{ messages.length }}) — oleku filter
        arvestab ainult neid. Kitsenda kuupäevi või vajuta „Laadi veel”.
      </v-alert>
      <v-alert v-if="error" type="warning" variant="tonal" density="compact" class="mt-4 mb-0">
        {{ error }}
      </v-alert>

      <v-data-table
        :headers="headers"
        :items="shown"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
        item-value="sid"
        :no-data-text="error ? '' : (statuses.length && messages.length ? 'Selle olekuga SMS-e valitud päevadel ei ole' : 'Valitud päevadel SMS-e ei saadetud')"
        class="mt-2 sms-table"
      >
        <template v-slot:[`item.dateSent`]="{ value }">
          <span class="text-no-wrap">{{ formatDate(value) }}</span>
        </template>
        <template v-slot:[`item.to`]="{ value }">
          <span class="text-no-wrap">{{ value }}</span>
        </template>
        <template v-slot:[`item.body`]="{ value }">
          <div class="sms-body">{{ value }}</div>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-tooltip v-if="item.errorCode" location="top">
            <template v-slot:activator="{ props }">
              <v-chip v-bind="props" size="x-small" variant="flat" :color="statusColor(item.status)">{{ statusText(item.status) }}</v-chip>
            </template>
            Viga {{ item.errorCode }}<template v-if="item.errorMessage">: {{ item.errorMessage }}</template>
          </v-tooltip>
          <v-chip v-else size="x-small" variant="flat" :color="statusColor(item.status)">{{ statusText(item.status) }}</v-chip>
        </template>
        <template v-slot:[`item.price`]="{ item }">
          <span class="text-no-wrap">{{ item.price === null ? '—' : money(Math.abs(item.price)) + ' ' + (item.priceUnit || '') }}</span>
        </template>
      </v-data-table>

      <div v-if="next" class="text-center pt-4">
        <v-btn variant="outlined" :loading="loading" @click="loadMore">Laadi veel</v-btn>
      </div>
    </card>
  </system-frame>
</template>

<script>
import SystemFrame from '@/components/SystemFrame.vue'
import api from '@/api'
import dayjs from 'dayjs'

// Twilio's statuses in the office's words. Anything not listed is shown as
// Twilio wrote it.
const STATUS = {
  delivered: { text: 'Kohale jõudnud', color: 'green-darken-1' },
  sent: { text: 'Saadetud', color: 'blue-grey-lighten-1' },
  queued: { text: 'Järjekorras', color: 'grey-lighten-1' },
  accepted: { text: 'Vastu võetud', color: 'grey-lighten-1' },
  sending: { text: 'Saatmisel', color: 'grey-lighten-1' },
  scheduled: { text: 'Ajastatud', color: 'grey-lighten-1' },
  undelivered: { text: 'Ei jõudnud kohale', color: 'red-darken-1' },
  failed: { text: 'Ebaõnnestus', color: 'red-darken-1' },
  canceled: { text: 'Tühistatud', color: 'grey' },
  received: { text: 'Vastu võetud', color: 'blue-grey-lighten-1' }
}

// Statuses always offered in the filter, even at zero; the rest appear when
// they turn up in the loaded SMS.
const COMMON = ['delivered', 'sent', 'undelivered', 'failed', 'queued']
// One page of Twilio's list, and how many the status filter fetches by itself.
const PAGE_SIZE = 200
const MAX_AUTO_PAGES = 25

export default {
  components: { SystemFrame },
  data () {
    return {
      balance: null,
      balanceLoading: false,
      balanceError: '',
      // The last seven days, today included.
      from: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
      messages: [],
      next: null,
      loading: false,
      error: '',
      // Only the newest request may fill the table.
      requestId: 0,
      // Twilio statuses chosen in the Olek filter; empty is all of them.
      statuses: [],
      // Bumped by every new auto-load, so a stale one stops.
      loadAllRun: 0,
      headers: [
        { title: 'Saadetud', key: 'dateSent' },
        { title: 'Number', key: 'to' },
        { title: 'Tekst', key: 'body', sortable: false },
        { title: 'Olek', key: 'status' },
        { title: 'Osad', key: 'numSegments', align: 'end' },
        { title: 'Hind', key: 'price', align: 'end' }
      ]
    }
  },
  computed: {
    // CSC: the top card is its balance and this month from intra's own log.
    csc () {
      return !!(this.balance && this.balance.provider === 'csc')
    },
    // Twilio's list cannot be asked by status — only by date and number — so
    // the filter is applied here, over every SMS of the chosen days (see
    // loadAll, which fetches the rest as soon as a status is picked).
    shown () {
      return this.statuses.length ? this.messages.filter(m => this.statuses.includes(m.status)) : this.messages
    },
    // The statuses Twilio uses, in the office's words, each with how many of
    // the loaded SMS have it. One that is not in the list still appears once
    // it turns up, under Twilio's own word.
    statusOptions () {
      const count = {}
      for (const m of this.messages) count[m.status] = (count[m.status] || 0) + 1
      const keys = [...new Set([...Object.keys(STATUS), ...Object.keys(count)])]
      return keys
        .filter(key => key && (count[key] || COMMON.includes(key) || this.statuses.includes(key)))
        .map(key => ({ value: key, text: this.statusText(key) + ' (' + (count[key] || 0) + ')' }))
    },
    // Twilio prices outgoing messages as negative amounts; the total is what
    // was spent, of what is shown. Null while none of them has a price yet.
    totalPrice () {
      const priced = this.shown.filter(m => m.price !== null)
      return priced.length ? priced.reduce((sum, m) => sum + Math.abs(m.price), 0) : null
    },
    priceUnit () {
      const m = this.shown.find(m => m.priceUnit)
      return m ? m.priceUnit : ''
    }
  },
  watch: {
    // Picking a status means "all of them in these days", not "of the first
    // page": fetch the rest of the range before filtering it.
    statuses (now) {
      if (now.length) this.loadAll()
    }
  },
  mounted () {
    this.loadBalance()
    this.load()
  },
  methods: {
    async loadBalance () {
      this.balanceLoading = true
      this.balanceError = ''
      try {
        this.balance = await api.getSmsBalance()
      } catch (err) {
        this.balance = null
        this.balanceError = (err && err.message) || 'Saldo küsimine ebaõnnestus'
      } finally {
        this.balanceLoading = false
      }
    },
    async load () {
      this.loadAllRun++
      this.messages = []
      this.next = null
      await this.fetchPage(null)
      if (this.statuses.length) await this.loadAll()
    },
    // The rest of the chosen days, page by page, so the status filter sees all
    // of them. Capped: a filter must not turn one click into a thousand calls
    // to Twilio — past the cap the page says so and "Laadi veel" carries on.
    async loadAll () {
      const run = ++this.loadAllRun
      let pages = 0
      while (this.next && pages < MAX_AUTO_PAGES && run === this.loadAllRun && !this.error) {
        await this.fetchPage(this.next)
        pages++
      }
    },
    async loadMore () {
      if (this.next) await this.fetchPage(this.next)
    },
    async fetchPage (next) {
      if (!this.from || !this.to) {
        this.error = 'Vali mõlemad kuupäevad'
        return
      }
      const id = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const answer = await api.getSmsList({
          from: this.from,
          to: this.to,
          pageSize: PAGE_SIZE,
          ...(next ? { pageToken: next.pageToken, page: next.page } : {})
        })
        if (id !== this.requestId) return
        this.messages = this.messages.concat(answer.messages || [])
        this.next = answer.next || null
      } catch (err) {
        if (id !== this.requestId) return
        this.error = (err && err.message) || 'SMS-ide küsimine ebaõnnestus'
      } finally {
        if (id === this.requestId) this.loading = false
      }
    },
    formatDate (date) {
      return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : ''
    },
    money (value) {
      return value === null || value === undefined || value === '' ? '' : Number(value).toFixed(4).replace(/0{1,2}$/, '')
    },
    euro (value) {
      return Number(value || 0).toFixed(2).replace('.', ',')
    },
    statusText (status) {
      return (STATUS[status] && STATUS[status].text) || status || ''
    },
    statusColor (status) {
      return (STATUS[status] && STATUS[status].color) || 'grey-lighten-1'
    }
  }
}
</script>

<style scoped>
.sms-body {
  white-space: pre-line;
  min-width: 220px;
  max-width: 480px;
  padding: 6px 0;
}
</style>
